import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Sidenavaction } from '../../../../assets/js/application/sidenavaction';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import { BatchLoader } from "../../../Utils/batch-loader.util";
import FarmSiteModel from 'src/app/services/farmSite.model';
import { FarmSiteService } from 'src/app/services/farmSite.service';
import { PondSectionService } from 'src/app/services/pondSection.service';
import CultureTypeModel from 'src/app/services/cultureType.model';
import CultureCategoryModel from 'src/app/services/cultureCategory.model';
import SpeciesTypeModel from 'src/app/services/speciesType.model';

import WaterTypeModel from 'src/app/services/waterType.model';
import SoilTypeModel from 'src/app/services/soilTypeModel';
import { CultureTypeService } from 'src/app/services/cultureType.service';
import { CultureCategoryService } from 'src/app/services/cultureCategory.service';
import { SpeciesTypeService } from 'src/app/services/speciesType.service';
import { WaterTypeService } from 'src/app/services/waterType.service';
import { SoilTypeService } from 'src/app/services/soilType.service';
import { CustomerPondService } from 'src/app/services/customerPond.service';
import CustomerPondModel from 'src/app/services/customerPond.model';
import { PondCultureService } from 'src/app/services/pondCulture.service';
import PondCultureModel from 'src/app/services/pondCulture.model';

declare var $: any;

/**
 * SensorComponent
 *
 * This component is responsible for managing Sensors within the Hardware Management module.
 * It provides a user interface for administrators to view, create, update, and search for sensors.
 * The component implements infinite scrolling for efficient data display and uses reactive forms for data entry.
 *
 * Key Responsibilities:
 * - Fetch and display a list of all sensors.
 * - Provide search functionality to filter sensors by name, vendor name, QP name, or sensor type name.
 * - Manage the creation of new sensors with validation.
 * - Handle updates to existing sensors.
 * - Implement infinite scrolling using BatchLoader to handle large datasets.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 *
 * Features:
 * - **CRUD Operations**: Create and Update sensors.
 * - **Real-time Search**: Filter list by multiple sensor attributes.
 * - **Infinite Scroll**: Load data in batches to improve performance.
 * - **Form Validation**: Ensures sensor details follow specific patterns (alphanumeric, spaces).
 * - **Date Formatting**: Formats 'updatedAt' timestamps for display.
 * - **Dynamic Dropdowns**: Loads sensor types and query parameters for selection.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches all sensors from `SensorService`.
 * 3. Fetches reference data (Sensor Types, Query Params).
 * 4. Formats dates using `moment`.
 * 5. Initializes `BatchLoader` with the full dataset.
 * 6. User interactions (scroll, search, add/edit) trigger state updates and service calls.
 * 7. `addUpdatePondCulture` sends data to backend and refreshes the list on success.
 *
 * @component
 * @selector app-sensor
 * @example
 * <app-sensor></app-sensor>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-pond-cultures',
  templateUrl: './pond-cultures.component.html',
  styleUrls: ['./pond-cultures.component.scss']
})
export class PondCulturesComponent implements OnInit, OnDestroy {
  /**
   * Array holding all fetched sensors from the backend.
   * Acts as the source of truth for the list before filtering.
   * @type {PondCultureModel[]}
   * @default []
   */
  public pondCultures: PondCultureModel[] = [];

  /**
   * Array holding the currently displayed sensors.
   * This list is populated by the BatchLoader (subset of sensors or filteredSensors).
   * @type {PondCultureModel[]}
   * @default []
   */
  public pondCulturesList: PondCultureModel[] = [];

  /**
   * Model for the current sensor being added or edited.
   * Bound to the form for data manipulation.
   * @type {PondCultureModel}
   */
  public pondCulture: PondCultureModel = new PondCultureModel();

  /**
   * Array holding sensors that match the current search criteria.
   * Used as the source for BatchLoader when filtering is active.
   * @type {PondCultureModel[]}
   * @default []
   */
  public filteredPondCultures: PondCultureModel[] = [];

  /**
   * Array of available Query Parameters for selection.
   * @type {Array<FarmSiteModel>}
   */
  public farmsites: Array<FarmSiteModel>;

  /**
   * Array of available Query Parameters for selection.
   * @type {Array<CultureTypeModel>}
   */
  public cultureTypes: Array<CultureTypeModel>;

  /**
   * Array of available Query Parameters for selection.
   * @type {Array<CultureCategoryModel>}
   */
  public cultureCategories: Array<CultureCategoryModel>;

  /**
   * Array of available Query Parameters for selection.
   * @type {Array<SpeciesTypeModel>}
   */
  public speciesTypes: Array<SpeciesTypeModel>;

  /**
   * Array of available Query Parameters for selection.
   * @type {Array<SpeciesTypeModel>}
   */
  public filTeredSpeciesTypes: Array<SpeciesTypeModel>;

  /**
  * Array of available Query Parameters for selection.
  * @type {Array<WaterTypeModel>}
  */
  public waterTypes: Array<WaterTypeModel>;

  /**
  * Array of available Query Parameters for selection.
  * @type {Array<SoilTypeModel>}
  */
  public soilTypes: Array<SoilTypeModel>;

  /**
* Array of available Query Parameters for selection.
* @type {Array<CustomerPondModel>}
*/
  public ponds: Array<CustomerPondModel>;

  /**
   * Current search query string entered by the user.
   * @type {string}
   */
  public search: string;

  /**
   * Flag indicating if the form is in 'Create' mode.
   * @type {boolean}
   */
  public submitButton: boolean;

  /**
   * Flag indicating if the form is in 'Update' mode.
   * @type {boolean}
   */
  public updateButton: boolean;

  /**
   * Flag to prevent double clicks on submit actions.
   * @type {boolean}
   */
  public sensorDoubleClick: boolean;

  /**
   * Flag indicating if no data is available for display.
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * Active page number (not actively used in current logic but declared).
   * @type {number}
   */
  public activePageNo: number;

  /**
   * Flag to trigger clearing of the search input field.
   * @type {boolean}
   * @default false
   */
  public clearSearchInput: boolean = false;

  /**
   * Reactive form group for sensor details.
   * @type {FormGroup}
   */
  public registerForm: FormGroup;

  /**
   * Flag indicating if the form has been submitted.
   * Used to trigger validation error display in the UI.
   * @type {boolean}
   * @default false
   */
  public submitted = false;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   * @default false
   */
  public enableDisablePageLoader: boolean = false;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active.
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data or no search results).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Timeout reference for search input debouncing.
   * @type {any}
   */
  searchTimeout: any;

  /**
   * Count of fields in the form, used for layout adjustments.
   * @type {number}
   * @default 0
   */
  public formFieldCount: number = 0;

  /**
   * Flag to toggle the visibility of the add/edit form.
   * @type {boolean}
   * @default false
   */
  public showForm: boolean = false;

  /**
   * Constructor for SensorComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * Form Validation Rules:
   * - name: Required, Min 3, Max 20, Pattern (alphabetic, spaces).
   * - vendor_name: Required, Min 3, Max 20, Pattern (alphabetic, spaces).
   * - qp_id: Required.
   * - sensor_type_id: Required.
   *
   * @constructor
   * @param {ToastrService} toastrService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {SensorService} sensorService - Service for Sensor API operations.
   * @service SensorService
   * @param {QueryParamsService} queryParamsService - Service for Query Params API operations.
   * @service QueryParamsService
   * @param {SensorTypeService} sensorTypeService - Service for Sensor Type API operations.
   * @service SensorTypeService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(
    private toastrService: ToastrService,
    public formBuilder: FormBuilder,
    public sidenavaction: Sidenavaction,
    public farmsiteService: FarmSiteService,
    private sectionsService: PondSectionService,
    private cultureTypeService: CultureTypeService,
    private cultureCategoryService: CultureCategoryService,
    private speciesTypeService: SpeciesTypeService,
    private waterTypeService: WaterTypeService,
    private soilTypeService: SoilTypeService,
    private pondsService: CustomerPondService,
    private pondCultureService: PondCultureService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService) {
    this.pondCulture = new PondCultureModel();
    this.registerForm = this.formBuilder.group({
      culture_category_id: ['', [Validators.required]],
      culture_type_id: ['', [Validators.required]],
      species_type_id: ['', [Validators.required]],
      water_type_id: ['', [Validators.required]],
      soil_type_id: ['', [Validators.required]],
      pond_id: ['', [Validators.required]],
      stocking_count: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      stocking_density: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      date_of_stocking: ['', [Validators.required]],
      time_of_stocking: ['08:00', [Validators.required]],
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Sets up side navigation.
   * 2. Resets component state and form.
   * 3. Tracks the current route.
   * 4. Fetches all sensors from the backend.
   * 5. Fetches reference data (Query Params, Sensor Types).
   * 6. Formats the 'updatedAt' date for each sensor.
   * 7. Initializes the batch loader and loads the first batch of data.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    try {
      window.scrollTo(0, 0);
      this.enableDisablePageLoader = true;
      this.clear();
      this.search = '';
      this.pondCultures = [];
      this.pondCulturesList = [];
      this.filteredPondCultures = [];
      this.isFiltering = false;
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      this.sidenavaction.x_navigation();

      const farmsites = await this.farmsiteService.getFarmeSitesByFarmId(localStorage.loginBusinessId);
      const ponds = await this.pondsService.getAllCustomerPonds();
      const pondCultures = await this.pondCultureService.getAllPondCultures();

      this.ponds = ponds
        .filter(s => farmsites.some(f => f.id === s.farm_site_id))

      this.pondCultures = pondCultures
        .filter(s => this.ponds.some(f => f.id === s.pond_id))

      this.cultureTypes = await this.cultureTypeService.getAllCultureTypes();
      this.cultureCategories = await this.cultureCategoryService.getAllCultureCategories();
      this.speciesTypes = await this.speciesTypeService.getAllSpeciesType();
      this.waterTypes = await this.waterTypeService.getAllWaterTypes();
      this.soilTypes = await this.soilTypeService.getAllSoilTypes();
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
      this.enableDisablePageLoader = false;
    }
  }

  getSpeciesByCultureCategory() {
    console.log('396', this.speciesTypes, this.registerForm.value.culture_category_id)
    this.filTeredSpeciesTypes = [];
    this.filTeredSpeciesTypes = this.speciesTypes.filter(s => Number(s.culture_category_id) === Number(this.registerForm.value.culture_category_id));
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredPondCultures : this.pondCultures,
      (updatedList) => {
        this.pondCulturesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Getter for easy access to form controls in the template.
   *
   * @method f
   * @returns {AbstractControl} Form controls
   */
  get f() {
    return this.registerForm.controls;
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests for all injected services.
   * - Destroys and resets the batch loader.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.sectionsService.cancelOnGoingHttpRequests();
    this.farmsiteService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader.reset();
  }

  /**
   * Filters the sensor list based on the search query.
   *
   * - Filters by name, vendor name, QP name, or sensor type name (case-insensitive).
   * - Updates `filteredSensors`.
   * - Resets the batch loader to display filtered results.
   *
   * @async
   * @method applySearch
   * @returns {Promise<void>}
   * @side-effect Updates `filteredSensors`, `sensorsList`, `noList`, `isFiltering`.
   */
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.pondCultures.filter(pc =>
        pc.pond?.name?.toLowerCase().includes(query)
      )
      : this.pondCultures;
    this.filteredPondCultures = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.pondCulturesList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * Debounces search input changes.
   * Calls `applySearch` after a delay to prevent excessive filtering.
   *
   * @method onSearchChanged
   * @returns {void}
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * Resets the form and component state to default values.
   *
   * @method clear
   * @returns {void}
   */
  public clear() {
    this.registerForm.reset({
      time_of_stocking: this.getCurrentTime()
    });

    this.showForm = false;
    this.pondCulture = new PondCultureModel();
    this.sensorDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
  }

  getCurrentTime(): string {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  /**
   * Handles the submission of the Sensor form (Add or Update).
   *
   * - Validates the form.
   * - Calls `create` or `updateOneByID` service methods based on mode.
   * - Displays success notification.
   * - Refreshes the list via `ngOnInit`.
   *
   * @async
   * @method addUpdatePondCulture
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async addUpdatePondCulture() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    // GET FORM VALUES
    const formData = this.registerForm.value; // contains updated values
    const loginUser = localStorage.getItem('LoginUserName');
    formData.created_name = loginUser;
    formData.updated_name = loginUser;
    try {
      this.sensorDoubleClick = true;
      if (this.submitButton) {
        this.pondCulture.culture_category_id = formData.culture_category_id;
        this.pondCulture.water_type_id = formData.water_type_id;
        this.pondCulture.soil_type_id = formData.soil_type_id;
        this.pondCulture.culture_type_id = formData.culture_type_id;
        this.pondCulture.species_type_id = formData.species_type_id;
        this.pondCulture.pond_id = formData.pond_id;
        this.pondCulture.start_date = formData.date_of_stocking
        ? formData.date_of_stocking.format('YYYY-MM-DD')
        : null;
        this.pondCulture.stocking_count = formData.stocking_count;
        this.pondCulture.stocking_density = formData.stocking_density;
        this.pondCulture.time_of_stocking = formData.time_of_stocking;
        this.pondCulture.date_of_stocking = formData.date_of_stocking
        ? formData.date_of_stocking.format('YYYY-MM-DD')
        : null;
        this.pondCulture.present_no_of_animals = formData.stocking_density;
        console.log('form data', formData, this.pondCulture);

        await this.pondCultureService.create(this.pondCulture);
        this.toastrService.success('Culture created successfully', '', {
          timeOut: 2000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
      this.sensorDoubleClick = false;
    }
  }

  /**
   * Retrieves a Sensor by ID and populates the form for editing.
   * Sets the component to 'Update' mode.
   *
   * @method getSensorById
   * @param {string} id - The ID of the sensor to edit.
   * @returns {void}
   */
  public getPondCultureById(id: string) {
    this.showForm = true;
    this.submitButton = false;
    this.sensorDoubleClick = false;
    this.updateButton = true;
    const foundSensors = this.pondCulturesList.find(uom => uom?.id === id);
    this.pondCulture = foundSensors ? { ...foundSensors } : new PondCultureModel(); // clone object
    if (this.pondCulture) {
      this.registerForm.patchValue({
        section_name: this.pondCulture.section_name,
        farm_site_id: this.pondCulture.farm_site_id,
      });
    }
  }

  /**
   * Opens the form in 'Create' mode.
   *
   * @method openForm
   * @returns {void}
   */
  public openForm() {
    this.registerForm.patchValue({
      time_of_stocking: this.getCurrentTime()
    });
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}
